<template>
    <div>
        <!-- 设备报警弹窗 -->
        <DevAlarmPop ref="DevAlarmPop" :mqttClient="mqttInfo.client"></DevAlarmPop>
        <!-- 消息中心 MQTT 订阅（mqttOnly 仅执行订阅，不渲染面板） -->
        <MessageCenterPoptip ref="AdminMessageNotice" :mqttClient="mqttInfo.client" mqtt-only />
    </div>
</template> 

<script>
import mqtt from 'mqtt'
import DevAlarmPop from './DevAlarmPop'
import MessageCenterPoptip from './MessageCenterPoptip'
import { mapState } from 'vuex'

export default {
  name: 'MqttClient',
  components: {
    DevAlarmPop,
    MessageCenterPoptip
  },
  data () {
    return {
      // mqtt连接信息
      mqttInfo: {
        client: null,
        reconnectTimer: null,
        reconnectAttempts: 0,
        maxReconnectAttempts: 10, // 增加最大重连次数
        reconnectInterval: 2000
      },
      // 保活机制相关
      keepAlive: {
        pingTimer: null,
        pingInterval: 25000, // 小于keepalive时间(30s)
        lastActivity: 0,
        pingTopic: 'client/ping/' + Math.random().toString(36).substr(2, 8)
      },
      refsArr: ['DevAlarmPop', 'AdminMessageNotice'],
      // 新增：标签页可见性监听
      visibilityHandler: null,
      networkMonitor: null,
      // 连接状态标志，防止重复连接
      isConnecting: false
    }
  },
  computed: {
    ...mapState(['labelType'])
  },
  watch: {
    labelType (newVal) {
      if (newVal === 0 || newVal === 2) {
        this.connect()
      } else {
        this.disconnect()
      }
    }
  },
  mounted () {
    // 标签页可见性监听
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        // 标签页激活时检查连接
        if ((this.labelType === 0 || this.labelType === 2) &&
                    (!this.mqttInfo.client || !this.mqttInfo.client.connected)) {
          this.connect()
        } else if (this.mqttInfo.client && this.mqttInfo.client.connected) {
          this.mqttInfo.client.publish(
            this.keepAlive.pingTopic,
            JSON.stringify({ type: 'visible_ping' }),
            { qos: 0, retain: false }
          )
        }
      } else {
        // 进入后台时，延迟20秒发送一次心跳（单次）
        if (this.mqttInfo.client && this.mqttInfo.client.connected) {
          setTimeout(() => {
            if (document.visibilityState === 'hidden') {
              this.mqttInfo.client.publish(
                this.keepAlive.pingTopic,
                JSON.stringify({ type: 'hidden_ping' }),
                { qos: 0, retain: false }
              )
            }
          }, 20000)
        }
      }
    }
    document.addEventListener('visibilitychange', this.visibilityHandler)

    if (this.labelType === 0 || this.labelType === 2) {
      this.connect()
    }
    window.addEventListener('beforeunload', this.handleBeforeUnload)
    this.setupNetworkMonitoring()
  },
  beforeDestroy () {
    this.cleanup()
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    this.removeNetworkMonitoring()
    // 移除可见性监听
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
    }
  },
  methods: {
    /**
         * 建立MQTT连接
         */
    connect () {
      // 如果已经连接，直接返回
      if (this.mqttInfo.client && this.mqttInfo.client.connected) {
        return
      }

      // 如果正在连接中，避免重复连接
      if (this.isConnecting) {
        console.log('正在连接中，跳过重复连接请求')
        return
      }

      this.isConnecting = true
      this.disconnect()

      // 连接选项优化
      this.mqttInfo.client = mqtt.connect('wss://' + location.hostname + '/mqtt', {
      // this.mqttInfo.client = mqtt.connect('ws://' + location.hostname + ':8083/mqtt', {
        protocolVersion: 4,
        keepalive: 30, // 保持30秒心跳间隔
        clientId: 'xl_client_' + Math.random().toString(16).substr(2, 8),
        clean: false, // 改为false，保持会话状态
        reconnectPeriod: 0, // 禁用内置重连，使用自定义逻辑
        connectTimeout: 15000, // 延长连接超时时间
        resubscribe: true // 重连后自动重新订阅
      })

      // 连接成功回调
      this.mqttInfo.client.on('connect', (connack) => {
        // console.log('MQTT连接成功')
        this.isConnecting = false
        this.mqttInfo.reconnectAttempts = 0
        this.startKeepAlive()

        // 延迟一下再订阅，确保连接完全建立
        setTimeout(() => {
          // 开启组件订阅
          this.refsArr.forEach(ref => {
            if (this.$refs[ref] && this.mqttInfo.client && this.mqttInfo.client.connected) {
              this.$refs[ref].subscribe()
            }
          })
        }, 100)
      })

      // 消息到达回调
      this.mqttInfo.client.on('message', (topic, message) => {
        this.keepAlive.lastActivity = Date.now()
        // 可以在这里添加消息处理逻辑
      })

      // 连接断开回调
      this.mqttInfo.client.on('close', () => {
        console.log('MQTT连接断开')
        this.isConnecting = false
        this.stopKeepAlive()
        if (this.labelType === 0 || this.labelType === 2) {
          this.scheduleReconnect()
        }
      })

      // 错误处理回调
      this.mqttInfo.client.on('error', (err) => {
        console.error('MQTT连接错误:', err)
        this.isConnecting = false
        // 忽略ECONNREFUSED错误的重复打印
        if (!err.message.includes('ECONNREFUSED')) {
          this.stopKeepAlive()
          if (this.labelType === 0 || this.labelType === 2) {
            this.scheduleReconnect()
          }
        }
      })

      // 重连事件监听
      this.mqttInfo.client.on('reconnect', () => {
        console.log('正在重连...')
      })
    },

    /**
         * 启动保活机制
         */
    startKeepAlive () {
      this.stopKeepAlive()
      this.keepAlive.lastActivity = Date.now()

      // 启动心跳定时器
      this.keepAlive.pingTimer = setInterval(() => {
        this.checkConnection()
      }, this.keepAlive.pingInterval)
    },

    /**
         * 检查连接状态并发送心跳
         */
    checkConnection () {
      if (!this.mqttInfo.client || !this.mqttInfo.client.connected) {
        return
      }

      // 检查是否长时间没有活动
      const now = Date.now()
      if (now - this.keepAlive.lastActivity > this.keepAlive.pingInterval * 2) {
        // console.warn('长时间无活动，尝试发送心跳')
      }

      // 发送心跳包
      try {
        this.mqttInfo.client.publish(
          this.keepAlive.pingTopic,
          JSON.stringify({ timestamp: now }), // 增加时间戳便于调试
          { qos: 0, retain: false }
        )
      } catch (e) {
        console.error('心跳发送失败:', e)
        // 发送失败时主动触发重连
        this.scheduleReconnect()
      }
    },

    /**
         * 设置网络状态监控
         */
    setupNetworkMonitoring () {
      this.networkMonitor = {
        onlineHandler: () => {
          console.log('网络恢复')
          if ((this.labelType === 0 || this.labelType === 2) &&
                        (!this.mqttInfo.client || !this.mqttInfo.client.connected)) {
            this.connect()
          }
        },
        offlineHandler: () => {
          console.warn('网络断开')
          this.stopKeepAlive()
        }
      }

      window.addEventListener('online', this.networkMonitor.onlineHandler)
      window.addEventListener('offline', this.networkMonitor.offlineHandler)
    },

    /**
* 移除网络状态监控
*/
    removeNetworkMonitoring () {
      // 替换 this.networkMonitor?.onlineHandler 为 this.networkMonitor && this.networkMonitor.onlineHandler
      if (this.networkMonitor && this.networkMonitor.onlineHandler) {
        window.removeEventListener('online', this.networkMonitor.onlineHandler)
      }
      if (this.networkMonitor && this.networkMonitor.offlineHandler) {
        window.removeEventListener('offline', this.networkMonitor.offlineHandler)
      }
    },

    /**
         * 断开MQTT连接
         */
    disconnect () {
      this.stopKeepAlive()
      this.isConnecting = false

      // 先取消所有组件的订阅
      this.refsArr.forEach(ref => {
        if (this.$refs[ref] && typeof this.$refs[ref].unsubscribe === 'function') {
          this.$refs[ref].unsubscribe()
        }
      })

      if (this.mqttInfo.client) {
        console.log('正在断开MQTT连接...')
        this.mqttInfo.client.end(false, (err) => {
          if (err) console.error('断开连接错误:', err)
        })
        this.mqttInfo.client = null
      }

      clearTimeout(this.mqttInfo.reconnectTimer)
    },

    /**
         * 安排重连任务
         */
    scheduleReconnect () {
      if (this.mqttInfo.reconnectAttempts >= this.mqttInfo.maxReconnectAttempts) {
        console.warn('已达到最大重连次数，将在30秒后再次尝试')
        // 达到最大次数后，延迟30秒再试
        setTimeout(() => {
          this.mqttInfo.reconnectAttempts = 0
          this.scheduleReconnect()
        }, 30000)
        return
      }

      clearTimeout(this.mqttInfo.reconnectTimer)

      this.mqttInfo.reconnectAttempts++

      // 指数退避策略，增加随机性避免网络拥塞
      const delay = Math.min(
        this.mqttInfo.reconnectInterval * Math.pow(2, this.mqttInfo.reconnectAttempts - 1) +
                Math.random() * 1000, // 增加随机延迟
        30000
      )

      console.log(`将在${Math.round(delay)}ms后尝试第${this.mqttInfo.reconnectAttempts}次重连...`)

      this.mqttInfo.reconnectTimer = setTimeout(() => {
        if (this.labelType === 0 || this.labelType === 2) {
          this.connect()
        }
      }, delay)
    },

    handleBeforeUnload () {
      console.log('页面即将刷新，断开MQTT连接')
      this.disconnect()
    },

    cleanup () {
      console.log('清理MQTT资源')
      this.disconnect()
      clearTimeout(this.mqttInfo.reconnectTimer)
    },
    stopKeepAlive () {
      if (this.keepAlive.pingTimer) {
        clearInterval(this.keepAlive.pingTimer)
        this.keepAlive.pingTimer = null
      }
    }
  }
}
</script>

<style>
/* 无样式 */
</style>



// WEBPACK FOOTER //
// src/components/msgNotice/MqttNotice.vue